// ─── HolidaysView — gestão de feriados (multi-país) ─────────────────
//
// Lista por ano × país (PT, AO) + CRUD. Usados no cálculo H.N./H.E. do
// BM: tempo de intervenção em dia feriado conta SEMPRE como Horas Extra.
//
// O país por defeito é lido do `clientSettings.defaultCountry` do tenant
// — Petrotec PT abre em PT, Petrotec AO abre em AO. O operador pode
// alternar para ver/editar o outro país.
//
// Nome do ficheiro continua `HolidaysAngolaView.js` por compat com os
// imports existentes; o título e dados são genéricos.
//
// Restrito a admin/super_admin (gate no backend); operador comum só vê.

import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, IconButton, Tooltip, Chip,
  CircularProgress, Select, MenuItem, FormControl, InputLabel,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import RefreshIcon from '@material-ui/icons/Refresh';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import EventIcon from '@material-ui/icons/Event';
import { useDispatch } from 'react-redux';
import { setActiveView } from '../../store/slices/uiSlice';
import { AuthContext } from '../../App';
import ConfirmDialog from '../common/ConfirmDialog';
import { useFormCardScroll } from '../../utils/manageHelpers';

const useStyles = makeStyles(() => ({
  root: { width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: '#f8fafc', overflow: 'hidden' },
  header: { display: 'flex', alignItems: 'center', gap: 12, padding: '16px 24px', background: '#fff', borderBottom: '1px solid #e2e8f0' },
  titleBox: { flex: 1, minWidth: 0 },
  title: { fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 },
  subtitle: { fontSize: '0.7rem', color: '#64748b', marginTop: 2 },
  toolbar: { padding: '10px 24px', background: '#fff', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  countryTabs: { display: 'flex', gap: 4 },
  countryTab: {
    padding: '6px 12px', borderRadius: 8, fontSize: '0.78rem', fontWeight: 700,
    cursor: 'pointer', border: '1px solid #e2e8f0', background: '#f8fafc', color: '#64748b',
    display: 'flex', alignItems: 'center', gap: 6,
  },
  countryTabActive: { background: '#dc2626 !important', color: '#fff !important', borderColor: '#b91c1c !important' },
  content: { flex: 1, overflowY: 'auto', padding: '16px 24px' },
  formCard: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '18px 22px', marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12 },
  formTitle: { fontSize: '0.86rem', fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 },
  formActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 },
  list: { background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' },
  listHeader: {
    display: 'grid', gridTemplateColumns: '120px 110px 1fr 100px 80px',
    gap: 8, padding: '10px 16px', background: '#f8fafc', borderBottom: '2px solid #e2e8f0',
    fontSize: '0.6rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em',
  },
  listRow: {
    display: 'grid', gridTemplateColumns: '120px 110px 1fr 100px 80px',
    gap: 8, padding: '12px 16px', alignItems: 'center', borderBottom: '1px solid #f1f5f9',
  },
  empty: { padding: 60, textAlign: 'center', color: '#94a3b8' },
}));

const TYPE_LABELS = {
  national:  { label: 'Nacional',  color: '#dc2626', bg: '#fee2e2' },
  religious: { label: 'Religioso', color: '#7c3aed', bg: '#ede9fe' },
  regional:  { label: 'Regional',  color: '#0ea5e9', bg: '#dbeafe' },
};

const DAY_NAMES = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

const COUNTRIES = [
  { code: 'PT', label: 'Portugal', flag: '🇵🇹' },
  { code: 'AO', label: 'Angola',   flag: '🇦🇴' },
];

const EMPTY = { date: '', name: '', type: 'national' };

function HolidaysView({ lang = 'pt' }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { token, userRole, clientSettings } = useContext(AuthContext);
  const canEdit = userRole === 'admin' || userRole === 'super_admin';
  const pt = lang === 'pt';

  // Default country vem do tenant (PT abre em PT, AO abre em AO).
  const defaultCountry = (clientSettings?.defaultCountry || 'AO').toUpperCase();
  const [country, setCountry] = useState(['PT', 'AO'].includes(defaultCountry) ? defaultCountry : 'AO');

  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const formCardRef = useFormCardScroll(editingId);

  const fetchHolidays = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError(null);
    try {
      const r = await fetch(`/planner-api/holidays-angola?year=${year}&countryCode=${country}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setHolidays(await r.json());
    } catch (e) {
      setError(pt ? 'Não foi possível carregar feriados.' : 'Could not load holidays.');
    } finally { setLoading(false); }
  }, [token, year, country, pt]);

  useEffect(() => { fetchHolidays(); }, [fetchHolidays]);

  function startCreate() { setForm(EMPTY); setEditingId('new'); }
  function startEdit(h) {
    setForm({ date: h.date, name: h.name, type: h.type });
    setEditingId(h.id);
  }
  function cancelEdit() { setEditingId(null); setForm(EMPTY); }

  async function saveForm() {
    if (!form.date || !/^\d{4}-\d{2}-\d{2}$/.test(form.date)) {
      setError(pt ? 'Data inválida' : 'Invalid date'); return;
    }
    if (!form.name.trim()) { setError(pt ? 'Nome obrigatório' : 'Name required'); return; }
    setSaving(true); setError(null);
    try {
      const isNew = editingId === 'new';
      const url = isNew ? '/planner-api/holidays-angola' : `/planner-api/holidays-angola/${editingId}`;
      const r = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: form.date, name: form.name.trim(), type: form.type, countryCode: country }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${r.status}`);
      }
      setEditingId(null); setForm(EMPTY);
      fetchHolidays();
    } catch (e) {
      setError(e.message);
    } finally { setSaving(false); }
  }

  async function handleDelete(id) {
    try {
      const r = await fetch(`/planner-api/holidays-angola/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Erro a apagar');
      setDeleteConfirm(null);
      fetchHolidays();
    } catch (e) { setError(e.message); }
  }

  const years = [];
  const currentY = new Date().getFullYear();
  for (let y = currentY - 1; y <= currentY + 3; y++) years.push(y);

  const activeCountry = COUNTRIES.find(c => c.code === country) || COUNTRIES[1];

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Tooltip title={pt ? 'Voltar a Manutenção' : 'Back'}>
          <IconButton size="small" onClick={() => dispatch(setActiveView('maintenance'))}>
            <ArrowBackIcon style={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
        <Box className={classes.titleBox}>
          <Typography className={classes.title}>
            <EventIcon style={{ fontSize: 20, color: '#dc2626' }} />
            {pt ? `Feriados ${activeCountry.label}` : `${activeCountry.label} Holidays`}
            <span style={{ marginLeft: 4 }}>{activeCountry.flag}</span>
          </Typography>
          <Typography className={classes.subtitle}>
            {pt
              ? 'Calendário usado no cálculo H.N./H.E. do BM — feriados contam sempre como Horas Extra'
              : 'Calendar for H.N./H.E. BM calculation — holidays always count as overtime'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={fetchHolidays} disabled={loading}>
          <RefreshIcon style={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <Box className={classes.toolbar}>
        {/* Tabs de país */}
        <Box className={classes.countryTabs}>
          {COUNTRIES.map(c => (
            <Box
              key={c.code}
              className={`${classes.countryTab} ${c.code === country ? classes.countryTabActive : ''}`}
              onClick={() => { setCountry(c.code); cancelEdit(); }}
            >
              <span>{c.flag}</span>
              <span>{c.label}</span>
            </Box>
          ))}
        </Box>
        <FormControl size="small" variant="outlined" style={{ minWidth: 120, marginLeft: 16 }}>
          <InputLabel>{pt ? 'Ano' : 'Year'}</InputLabel>
          <Select label={pt ? 'Ano' : 'Year'} value={year} onChange={e => setYear(e.target.value)}>
            {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </Select>
        </FormControl>
        <Box style={{ flex: 1 }} />
        {canEdit && (
          <Button onClick={startCreate} startIcon={<AddIcon style={{ fontSize: 16 }} />}
            disabled={editingId !== null}
            style={{ textTransform: 'none', fontSize: '0.78rem', fontWeight: 700,
              background: '#dc2626', color: '#fff', padding: '6px 14px', borderRadius: 8 }}>
            {pt ? `Adicionar feriado ${activeCountry.label}` : `Add ${activeCountry.label} holiday`}
          </Button>
        )}
      </Box>

      <Box className={classes.content}>
        {error && (
          <Box style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 8, marginBottom: 12, fontSize: '0.78rem' }}>
            {error}
          </Box>
        )}

        {editingId !== null && (
          <Box ref={formCardRef} className={classes.formCard}>
            <Typography className={classes.formTitle}>
              <EventIcon style={{ fontSize: 18, color: '#dc2626' }} />
              {editingId === 'new'
                ? (pt ? `Novo feriado · ${activeCountry.label}` : `New holiday · ${activeCountry.label}`)
                : (pt ? `Editar feriado · ${activeCountry.label}` : `Edit holiday · ${activeCountry.label}`)}
            </Typography>
            <Box style={{ display: 'grid', gridTemplateColumns: '180px 1fr 160px', gap: 12 }}>
              <TextField type="date" label={pt ? 'Data' : 'Date'} size="small" variant="outlined"
                value={form.date} onChange={e => setForm({ ...form, date: e.target.value })}
                InputLabelProps={{ shrink: true }} />
              <TextField label={pt ? 'Designação' : 'Name'} size="small" variant="outlined"
                value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder={country === 'PT' ? 'Ex.: Dia de Portugal' : 'Ex.: Dia da Independência'} autoFocus />
              <FormControl size="small" variant="outlined">
                <InputLabel>{pt ? 'Tipo' : 'Type'}</InputLabel>
                <Select label={pt ? 'Tipo' : 'Type'} value={form.type}
                  onChange={e => setForm({ ...form, type: e.target.value })}>
                  <MenuItem value="national">Nacional</MenuItem>
                  <MenuItem value="religious">Religioso</MenuItem>
                  <MenuItem value="regional">Regional</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box className={classes.formActions}>
              <Button onClick={cancelEdit} size="small"
                style={{ textTransform: 'none', fontSize: '0.78rem' }}>
                {pt ? 'Cancelar' : 'Cancel'}
              </Button>
              <Button onClick={saveForm} disabled={saving} size="small" variant="contained"
                style={{ textTransform: 'none', fontSize: '0.78rem', background: '#dc2626', color: '#fff' }}>
                {saving ? <CircularProgress size={16} style={{ color: '#fff' }} /> : (pt ? 'Guardar' : 'Save')}
              </Button>
            </Box>
          </Box>
        )}

        {loading && holidays.length === 0 ? (
          <Box className={classes.empty}><CircularProgress size={24} /></Box>
        ) : holidays.length === 0 ? (
          <Box className={classes.empty}>
            <EventIcon style={{ fontSize: 40, color: '#cbd5e1', marginBottom: 8 }} />
            <Typography style={{ fontSize: '0.86rem', fontWeight: 600 }}>
              {pt ? `Sem feriados em ${activeCountry.label} ${year}` : `No ${activeCountry.label} holidays in ${year}`}
            </Typography>
          </Box>
        ) : (
          <Box className={classes.list}>
            <Box className={classes.listHeader}>
              <span>{pt ? 'Data' : 'Date'}</span>
              <span>{pt ? 'Dia da semana' : 'Weekday'}</span>
              <span>{pt ? 'Designação' : 'Name'}</span>
              <span>{pt ? 'Tipo' : 'Type'}</span>
              <span style={{ textAlign: 'right' }}>{pt ? 'Acções' : 'Actions'}</span>
            </Box>
            {holidays.map(h => {
              const d = new Date(h.date + 'T12:00:00');
              const weekday = DAY_NAMES[d.getDay()];
              const typeMeta = TYPE_LABELS[h.type] || TYPE_LABELS.national;
              return (
                <Box key={h.id} className={classes.listRow}>
                  <Typography style={{ fontSize: '0.86rem', fontWeight: 700, color: '#0f172a' }}>
                    {d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' })}
                  </Typography>
                  <Typography style={{ fontSize: '0.74rem', color: '#64748b' }}>
                    {weekday}
                  </Typography>
                  <Typography style={{ fontSize: '0.84rem', color: '#0f172a' }}>
                    {h.name}
                  </Typography>
                  <Chip label={typeMeta.label} size="small"
                    style={{ height: 20, fontSize: '0.62rem', fontWeight: 700, background: typeMeta.bg, color: typeMeta.color, width: 'fit-content' }} />
                  <Box style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                    {canEdit && (
                      <>
                        <Tooltip title={pt ? 'Editar' : 'Edit'}>
                          <IconButton size="small" onClick={() => startEdit(h)} disabled={editingId !== null}>
                            <EditIcon style={{ fontSize: 16, color: '#3b82f6' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={pt ? 'Apagar' : 'Delete'}>
                          <IconButton size="small" onClick={() => setDeleteConfirm(h)}>
                            <DeleteIcon style={{ fontSize: 16, color: '#ef4444' }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      <ConfirmDialog
        open={!!deleteConfirm}
        title={pt ? 'Apagar feriado?' : 'Delete holiday?'}
        message={deleteConfirm ? (pt
          ? `"${deleteConfirm.name}" (${deleteConfirm.date}) será apagado. Esta acção é irreversível.`
          : `"${deleteConfirm.name}" will be deleted.`) : ''}
        confirmText={pt ? 'Apagar' : 'Delete'}
        confirmColor="error"
        onConfirm={() => deleteConfirm && handleDelete(deleteConfirm.id)}
        onCancel={() => setDeleteConfirm(null)}
      />
    </Box>
  );
}

export default HolidaysView;
